import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Crown, Car, UsersRound, UserCog, Check, ArrowUpRight, Sparkles,
  ShieldCheck, Zap, X, Phone, Mail, BadgeCheck, Copy, ScanLine,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import PageHeader from '../components/layout/PageHeader';
import { useAuth } from '../context/AuthContext';
import { carAPI, userAPI, platformAPI } from '../services/api';
import { PLANS, getPlan, upgradesFrom, fmtLimit, audienceOf, showsDrivers, limitChips, planFeatures, vehicleNoun, activePlanId, isExpired } from '../config/plans';
import appConfig from '../config/appConfig';

const inr = (n) => new Intl.NumberFormat('en-IN').format(n);
const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

const DURATION_TEXT = {
  lifetime: 'for life',
  'first-year': 'for your first year',
  'first-payment': 'on your first payment',
};

// Usage meter with a progress bar (turns amber/red as it fills).
function UsageMeter({ icon: Icon, label, used, limit }) {
  const unlimited = limit === Infinity;
  const pct = unlimited ? 6 : Math.min(100, Math.round((used / Math.max(limit, 1)) * 100));
  const near = !unlimited && pct >= 80;
  const full = !unlimited && used >= limit;
  const barColor = full ? '#ef4444' : near ? '#f59e0b' : '#2563eb';
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-600">
          <span className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center"><Icon className="w-4 h-4" /></span>
          {label}
        </span>
        <span className="text-sm font-bold text-gray-900">
          {used}<span className="text-gray-400 font-medium"> / {unlimited ? '∞' : limit}</span>
        </span>
      </div>
      <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: barColor }} />
      </div>
      {full && <p className="text-xs text-red-600 mt-2 font-medium">Limit reached — upgrade to add more.</p>}
      {!full && near && <p className="text-xs text-amber-600 mt-2 font-medium">You’re close to your limit.</p>}
      {unlimited && <p className="text-xs text-gray-400 mt-2">Unlimited on your plan.</p>}
    </div>
  );
}

export default function Subscription() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Show the plan they're actually *on*, not the one they last paid for — an
  // expired plan behaves as Free everywhere in the backend, so it must here too.
  const sub = user?.subscription;
  const lapsedFrom = isExpired(sub) ? getPlan(sub.plan) : null;
  const planId = activePlanId(sub);
  const plan = getPlan(planId);
  const upgrades = upgradesFrom(planId);
  const expiresAt = sub?.expiresAt;

  const [usage, setUsage] = useState({ vehicles: 0, users: 1, drivers: 0, vehicleLimit: null, loading: true });
  const audience = audienceOf(user?.businessType);
  const [discount, setDiscount] = useState({ eligible: false, percent: 0, label: '', duration: 'lifetime' });
  const [support, setSupport] = useState({
    phone: appConfig.contact?.phone || '',
    email: appConfig.contact?.email || '',
  });
  const [wantPlan, setWantPlan] = useState(null); // plan the owner is requesting to upgrade to

  // Discounted amount for eligible owners (rounded to nearest rupee).
  const withDiscount = (amount) => (discount.eligible && amount > 0
    ? Math.round(amount * (1 - discount.percent / 100)) : amount);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [carsRes, staffRes, settingsRes] = await Promise.allSettled([
          carAPI.getAllCars(),
          userAPI.getStaffMembers(),
          platformAPI.getSettings(),
        ]);
        // Support contact (for manual upgrade requests) + founder discount config
        const settings = settingsRes.status === 'fulfilled' ? settingsRes.value.data?.settings : null;
        if (settings && alive) {
          const phone = settings.appConfig?.supportPhone || settings.supportContact?.phone;
          const email = settings.appConfig?.supportEmail || settings.supportContact?.email;
          setSupport((s) => ({ phone: phone || s.phone, email: email || s.email }));
        }
        const fd = settings?.founderDiscount || null;
        if (fd?.enabled && alive) {
          const cutoff = fd.cutoffDate ? new Date(fd.cutoffDate) : null;
          const joined = user?.createdAt ? new Date(user.createdAt) : null;
          const eligible = !cutoff || !joined || joined <= cutoff;
          if (eligible) setDiscount({ eligible: true, percent: fd.percent || 0, label: fd.label || 'Founding member offer', duration: fd.duration || 'lifetime' });
        }
        const cars = carsRes.status === 'fulfilled'
          ? (carsRes.value.data?.cars || carsRes.value.data?.data || carsRes.value.data || []) : [];
        // The backend is the authority on the cap — the team can raise an owner's
        // vehicle limit above their plan's default. `limit: null` means unlimited.
        const planUsage = carsRes.status === 'fulfilled' ? carsRes.value.data?.planUsage : null;
        const staff = staffRes.status === 'fulfilled'
          ? (staffRes.value.data?.staff || staffRes.value.data?.data || staffRes.value.data || []) : [];
        // Drivers are staff with role 'driver' — count them separately so they
        // don't inflate the team-members meter.
        const staffList = Array.isArray(staff) ? staff : [];
        const drivers = staffList.filter((s) => s.role === 'driver');
        if (alive) setUsage({
          vehicles: Array.isArray(cars) ? cars.length : 0,
          users: (staffList.length - drivers.length) + 1, // + the owner
          drivers: drivers.length,
          vehicleLimit: planUsage ? (planUsage.limit === null ? Infinity : planUsage.limit) : null,
          loading: false,
        });
      } catch {
        if (alive) setUsage((u) => ({ ...u, loading: false }));
      }
    })();
    return () => { alive = false; };
  }, []);

  const vehicleLimit = usage.vehicleLimit ?? plan.limits.vehicles;
  const customLimit = !usage.loading && vehicleLimit !== plan.limits.vehicles;

  const priceLabel = plan.custom ? 'Custom pricing'
    : plan.monthly === 0 ? 'Free forever'
    : `₹${inr(withDiscount(plan.monthly))}/mo`;

  return (
    <div>
      <PageHeader title="Subscription" description="Your current plan, usage and upgrades" />

      {/* Current plan hero */}
      <div className={`rounded-3xl p-6 sm:p-8 text-white shadow-xl bg-gradient-to-br ${plan.gradient} relative overflow-hidden`}>
        <div className="absolute -right-8 -top-8 opacity-10"><Crown className="w-40 h-40" /></div>
        <div className="relative">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-white/70">Current plan</span>
            {lapsedFrom ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-red-500/30 text-red-50 rounded-full px-2 py-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400" /> {lapsedFrom.name} expired
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-white/20 rounded-full px-2 py-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" /> Active
              </span>
            )}
          </div>
          <div className="flex items-end gap-3 flex-wrap">
            <h2 className="text-3xl sm:text-4xl font-extrabold">{plan.name}</h2>
            <span className="text-lg font-semibold text-white/80 mb-1">{priceLabel}</span>
          </div>
          <p className="text-white/70 mt-1">{plan.tagline}</p>
          {discount.eligible && (
            <div className="inline-flex items-center gap-1.5 mt-3 bg-amber-400/20 text-amber-100 border border-amber-300/30 rounded-full px-3 py-1 text-xs font-semibold">
              <Sparkles className="w-3.5 h-3.5" /> {discount.label} — {discount.percent}% off {DURATION_TEXT[discount.duration]}
            </div>
          )}
          {expiresAt && (
            <p className={`text-sm mt-3 ${lapsedFrom ? 'text-red-100 font-medium' : 'text-white/60'}`}>
              {lapsedFrom
                ? `Your ${lapsedFrom.name} plan expired on ${fmtDate(expiresAt)} — renew to restore it.`
                : `Renews / expires on ${fmtDate(expiresAt)}`}
            </p>
          )}
          {customLimit && (
            <p className="text-white/60 text-sm mt-1">
              Your account has a custom limit of {fmtLimit(vehicleLimit)} {vehicleNoun(audience)}.
            </p>
          )}
          <div className="flex flex-wrap gap-2 mt-5">
            {limitChips(plan, audience, { vehicles: vehicleLimit }).map((chip) => (
              <span key={chip} className="text-xs font-medium bg-white/15 rounded-full px-3 py-1.5">{chip}</span>
            ))}
          </div>
          {upgrades.length > 0 && (
            <button onClick={() => setWantPlan(lapsedFrom || upgrades.find((u) => u.popular) || upgrades[0])}
              className="mt-6 inline-flex items-center gap-2 bg-white text-gray-900 font-bold px-5 py-2.5 rounded-xl hover:bg-gray-100 transition-colors shadow">
              <Zap className="w-4 h-4" /> {lapsedFrom ? `Renew ${lapsedFrom.name}` : 'Upgrade plan'}
            </button>
          )}
        </div>
      </div>

      {/* Usage */}
      <h3 className="text-lg font-bold text-gray-900 mt-8 mb-3">Your usage</h3>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <UsageMeter icon={Car} label={audience === 'sale' ? 'Listings' : 'Vehicles'} used={usage.vehicles} limit={vehicleLimit} />
        <UsageMeter icon={UsersRound} label="Team members" used={usage.users} limit={plan.limits.users} />
        {showsDrivers(audience) && (
          <UsageMeter icon={UserCog} label="Drivers" used={usage.drivers} limit={plan.limits.drivers} />
        )}
      </div>

      {/* What's included */}
      <div className="mt-8 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-blue-600" /> What’s included in {plan.name}</h3>
        <div className="grid sm:grid-cols-2 gap-2.5">
          {planFeatures(plan, audience).map((f) => (
            <div key={f} className="flex items-start gap-2 text-sm text-gray-700">
              <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {f}
            </div>
          ))}
        </div>
      </div>

      {/* Upgrade options */}
      {upgrades.length > 0 ? (
        <>
          <div className="flex items-center justify-between mt-10 mb-3">
            <h3 className="text-lg font-bold text-gray-900">Upgrade for more</h3>
            <button onClick={() => navigate('/pricing')} className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1">
              Compare all plans <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {upgrades.map((p) => (
              <div key={p.id} className={`rounded-2xl bg-white border p-6 flex flex-col ${p.popular ? 'border-indigo-300 ring-1 ring-indigo-200 shadow-lg' : 'border-gray-200 shadow-sm'}`}>
                {p.popular && <span className="self-start inline-flex items-center gap-1 text-[11px] font-bold text-indigo-700 bg-indigo-50 rounded-full px-2 py-0.5 mb-2"><Sparkles className="w-3 h-3" /> Most Popular</span>}
                <h4 className="font-bold text-gray-900">{p.name}</h4>
                <div className="mt-1 flex items-end gap-2">
                  <div className="text-2xl font-extrabold text-gray-900">
                    {p.custom ? 'Custom' : p.monthly === 0 ? '₹0' : `₹${inr(withDiscount(p.monthly))}`}
                    {!p.custom && p.monthly > 0 && <span className="text-sm font-medium text-gray-400">/mo</span>}
                  </div>
                  {discount.eligible && !p.custom && p.monthly > 0 && (
                    <span className="text-sm text-gray-400 line-through mb-0.5">₹{inr(p.monthly)}</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  <span className="text-[11px] font-medium text-gray-600 bg-gray-100 rounded-full px-2 py-0.5">{fmtLimit(p.limits.vehicles)} {vehicleNoun(audience)}</span>
                  <span className="text-[11px] font-medium text-gray-600 bg-gray-100 rounded-full px-2 py-0.5">{fmtLimit(p.limits.users)} users</span>
                </div>
                <p className="text-xs text-gray-500 mt-3 flex-1">{planFeatures(p, audience)[0]}</p>
                <button onClick={() => setWantPlan(p)}
                  className={`mt-4 w-full py-2.5 rounded-xl font-semibold text-sm inline-flex items-center justify-center gap-1.5 ${p.popular ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}>
                  {p.custom ? 'Contact sales' : 'Upgrade'} <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="mt-10 bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-100 rounded-2xl p-6 text-center">
          <Crown className="w-8 h-8 text-amber-500 mx-auto mb-2" />
          <p className="font-bold text-gray-900">You’re on our top plan 🎉</p>
          <p className="text-sm text-gray-500 mt-1">You have access to everything {PLANS.Enterprise.name} offers.</p>
        </div>
      )}

      <p className="text-center text-xs text-gray-400 mt-8">
        To upgrade, pick a plan and our team will activate it for you. Prices exclusive of GST.
      </p>

      {wantPlan && (
        <UpgradeModal
          plan={wantPlan}
          discount={discount}
          support={support}
          user={user}
          onClose={() => setWantPlan(null)}
        />
      )}
    </div>
  );
}

// Each scheme replaces the `upi://` prefix of the deep link, so the rest of the
// query string (pa/pn/am/cu/tn) is carried through untouched.
const UPI_APPS = [
  { label: 'GPay', scheme: 'tez://upi/' },
  { label: 'PhonePe', scheme: 'phonepe://' },
  { label: 'Any UPI', scheme: 'upi://' },
];

// Build a UPI deep link / QR payload: upi://pay?pa=<vpa>&pn=<name>&am=<amount>&cu=INR&tn=<note>
const upiString = ({ upiId, payeeName, amount, note }) => {
  const p = new URLSearchParams();
  p.set('pa', upiId);
  p.set('pn', payeeName);
  if (amount > 0) p.set('am', String(amount));
  p.set('cu', 'INR');
  if (note) p.set('tn', note);
  return `upi://pay?${p.toString()}`;
};

// Upgrade modal — pay now by scanning the UPI QR, then confirm via call/email
// so the team can activate the plan (no payment gateway yet).
function UpgradeModal({ plan, discount, support, user, onClose }) {
  const [cycle, setCycle] = useState('monthly');
  const [copied, setCopied] = useState(false);

  const eligible = discount.eligible && !plan.custom;
  const disc = (n) => (eligible && n > 0 ? Math.round(n * (1 - discount.percent / 100)) : n);
  const monthly = disc(plan.monthly);
  const annual = disc(plan.annual);
  const hasAnnual = !plan.custom && plan.annual > 0;
  const amount = plan.custom ? 0 : (cycle === 'annual' ? annual : monthly);

  const upiId = appConfig.payment?.upiId || '';
  const payeeName = appConfig.payment?.payeeName || appConfig.appName || 'Wheels4Rent';
  const note = `${plan.name} plan${cycle === 'annual' ? ' (yearly)' : ''} - ${user?.name || 'owner'}`;
  const upiUri = upiId ? upiString({ upiId, payeeName, amount, note }) : '';

  const copyUpi = () => {
    if (!upiId) return;
    navigator.clipboard?.writeText(upiId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }).catch(() => {});
  };

  const confirmMsg = `Hi, I'm ${user?.name || 'an owner'} on Wheels4Rent. I've paid ₹${inr(amount)} for the ${plan.name} plan (${cycle}). Please activate it. My registered email: ${user?.email || ''}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* header */}
        <div className={`relative p-6 text-white bg-gradient-to-br ${plan.gradient}`}>
          <button onClick={onClose} className="absolute right-4 top-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
            <X className="w-4 h-4" />
          </button>
          <p className="text-xs font-semibold uppercase tracking-wider text-white/70">You selected</p>
          <div className="flex items-end gap-2 mt-1">
            <h3 className="text-2xl font-extrabold">{plan.name}</h3>
            {plan.popular && <span className="text-[11px] font-bold bg-white/20 rounded-full px-2 py-0.5 mb-1">Most Popular</span>}
          </div>
          {eligible && (
            <div className="inline-flex items-center gap-1.5 mt-2 bg-amber-400/20 text-amber-100 border border-amber-300/30 rounded-full px-2.5 py-1 text-[11px] font-semibold">
              <Sparkles className="w-3 h-3" /> {discount.percent}% {discount.label} applied
            </div>
          )}
        </div>

        {/* body */}
        <div className="p-6">
          {plan.custom ? (
            // Enterprise: no fixed price — contact only
            <div className="text-center">
              <p className="text-gray-700 text-sm mb-4">Enterprise is custom-priced. Reach out and we'll tailor a plan and share payment details.</p>
            </div>
          ) : (
            <>
              {/* billing cycle toggle */}
              {hasAnnual && (
                <div className="flex bg-gray-100 rounded-xl p-1 mb-4">
                  {['monthly', 'annual'].map((c) => (
                    <button key={c} onClick={() => setCycle(c)}
                      className={`flex-1 py-2 rounded-lg text-sm font-semibold capitalize transition-colors ${cycle === c ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
                      {c === 'annual' ? 'Yearly' : 'Monthly'}
                      {c === 'annual' && <span className="ml-1 text-[10px] text-emerald-600 font-bold">save {Math.round((1 - annual / (monthly * 12)) * 100)}%</span>}
                    </button>
                  ))}
                </div>
              )}

              {/* amount */}
              <div className="text-center mb-4">
                <div className="flex items-end justify-center gap-2">
                  <span className="text-4xl font-extrabold text-gray-900">₹{inr(amount)}</span>
                  <span className="text-gray-400 font-medium mb-1.5 text-sm">/{cycle === 'annual' ? 'year' : 'month'}</span>
                </div>
                {eligible && (
                  <span className="text-sm text-gray-400 line-through">₹{inr(cycle === 'annual' ? plan.annual : plan.monthly)}</span>
                )}
              </div>

              {/* QR */}
              {upiUri ? (
                <div className="flex flex-col items-center">
                  {/* A QR is useless on the device you're paying from — phones get
                      deep links instead, desktops get the code to scan. */}
                  <div className="hidden sm:flex flex-col items-center">
                    <div className="p-3 bg-white rounded-2xl border-2 border-gray-100 shadow-sm">
                      <QRCodeSVG value={upiUri} size={190} level="M" includeMargin={false} />
                    </div>
                    <p className="flex items-center gap-1.5 text-xs text-gray-500 mt-3 font-medium">
                      <ScanLine className="w-4 h-4 text-blue-600" /> Scan with any UPI app (GPay / PhonePe / Paytm)
                    </p>
                  </div>
                  <div className="w-full grid grid-cols-3 gap-2 sm:hidden">
                    {UPI_APPS.map((app) => (
                      <a key={app.label} href={upiUri.replace('upi://', app.scheme)}
                        className="flex items-center justify-center py-3 rounded-xl border-2 border-gray-200 text-xs font-semibold text-gray-700 hover:border-gray-300 transition-colors">
                        {app.label}
                      </a>
                    ))}
                  </div>
                  <button onClick={copyUpi}
                    className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg px-3 py-2 transition-colors">
                    <Copy className="w-4 h-4" /> {copied ? 'Copied!' : upiId}
                  </button>
                </div>
              ) : (
                <p className="text-sm text-center text-amber-600 bg-amber-50 border border-amber-100 rounded-xl p-3">
                  Payment UPI ID not configured yet. Please contact us using the options below.
                </p>
              )}
            </>
          )}

          {/* after-payment confirmation */}
          <div className="mt-5 pt-5 border-t border-gray-100">
            <p className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-1">
              <BadgeCheck className="w-4 h-4 text-emerald-500" /> {plan.custom ? 'Get in touch' : 'After paying, confirm with us'}
            </p>
            <p className="text-xs text-gray-500 mb-3">We'll verify the payment and activate your plan — usually within a few hours.</p>
            <div className="space-y-2.5">
              {support.phone && (
                <a href={`tel:${support.phone}`}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-sm">
                  <Phone className="w-4 h-4" /> Call {support.phone}
                </a>
              )}
              {support.email && (
                <a href={`mailto:${support.email}?subject=${encodeURIComponent(`Activate ${plan.name} plan`)}&body=${encodeURIComponent(confirmMsg)}`}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-gray-800 bg-gray-100 hover:bg-gray-200 transition-colors">
                  <Mail className="w-4 h-4" /> Email us
                </a>
              )}
              {!support.phone && !support.email && (
                <p className="text-sm text-center text-gray-500 py-2">
                  Please contact your administrator to activate the {plan.name} plan.
                </p>
              )}
            </div>
          </div>

          <p className="text-center text-[11px] text-gray-400 mt-4">
            Manual activation — no auto-charge or recurring billing.
          </p>
        </div>
      </div>
    </div>
  );
}
